import { format, parseISO } from 'date-fns';
import { ShoppingBag } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { useDateRange } from '../context/DateRangeContext';
import { useAssistedRevenueSummary } from './hooks/useAssistedRevenueSummary.hook';
import {
  RECENT_ORDERS_PAGE_SIZE,
  useRecentAssistedOrders,
} from './hooks/useRecentAssistedOrders.hook';
import { IndexPath } from '@/Config/app/router/index.path';
import {
  AssistedRevenueSummaryDto,
  DailyRevenuePointDto,
} from '@/Domain/api/AssistedOrders/AssistedOrders.dto';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/Domain/components/Shared/Card/Card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/Domain/components/Shared/Chart/chart';
import { ChartPlaceholder } from '@/Domain/components/Shared/ChartPlaceholder';
import { DateRangeControls } from '@/Domain/components/Shared/DateRangeControls/DateRangeControls';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/Domain/components/Shared/Pagination/pagination';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { DateRangeState } from '@/Domain/types/DateRange.types';

// UTMs the digest-email CTA (quickchat/activity_summary/send.py) stamps on the deep-link; read into the view event, then stripped so an in-app return to this tab doesn't re-report via_email.
const DIGEST_UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'] as const;

const formatCurrencyValue = (amount: string, currency: string): string => {
  const num = Number(amount);
  if (Number.isNaN(num)) return amount;
  const hasDecimal = num % 1 !== 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: hasDecimal ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${amount} ${currency}`;
  }
};

/** All currencies on one line, separated by a dot — a store with a single
 * currency (the common case) reads as one clean number. */
const InlineCurrencyAmounts = ({
  entries,
  className,
  testIdPrefix,
}: {
  entries: [currency: string, amount: string][];
  className: string;
  testIdPrefix?: string;
}) => (
  <div className={`flex flex-wrap items-baseline gap-x-2 ${className}`}>
    {entries.map(([currency, amount], i) => (
      <Fragment key={currency}>
        {i > 0 && <span className="text-muted-foreground">·</span>}
        <span data-testid={testIdPrefix ? `${testIdPrefix}-${currency}` : undefined}>
          {formatCurrencyValue(amount, currency)}
        </span>
      </Fragment>
    ))}
  </div>
);

type DailyByCurrency = {
  day: string;
  totalsByCurrency: Record<string, number>;
};

const groupDailyByCurrency = (points: DailyRevenuePointDto[]): DailyByCurrency[] => {
  const byDay = new Map<string, DailyByCurrency>();
  for (const p of points) {
    if (!byDay.has(p.day)) {
      byDay.set(p.day, { day: p.day, totalsByCurrency: {} });
    }
    byDay.get(p.day)!.totalsByCurrency[p.currency] = Number(p.total_amount);
  }
  return Array.from(byDay.values()).sort((a, b) => a.day.localeCompare(b.day));
};

/** Round a value up to a "nice" 1/2/5·10ⁿ step so the Y axis lands on round,
 * evenly-spaced ticks (e.g. 0/100/200/300/400) instead of Recharts' raw
 * data-max ticks (0/85/170/255/340), and the peak isn't glued to the top. */
const niceAxisStep = (value: number): number => {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
};

const HeroNumbers = ({ summary }: { summary: AssistedRevenueSummaryDto }) => {
  const { t } = useTranslation('insights_page', { keyPrefix: 'assisted_revenue' });
  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
      <Card className="md:col-span-2">
        <CardHeader>
          <CardDescription>{t('hero_label')}</CardDescription>
          <InlineCurrencyAmounts
            className="text-3xl font-semibold tracking-tight"
            testIdPrefix="hero-gmv"
            entries={summary.by_currency.map((row) => [row.currency, row.total_amount])}
          />
          <CardDescription className="pt-1">
            {t('total_orders', { count: summary.total_orders })}
          </CardDescription>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardDescription>{t('average_order_value')}</CardDescription>
          <InlineCurrencyAmounts
            className="text-xl font-semibold"
            entries={Object.entries(summary.average_order_value_by_currency)}
          />
        </CardHeader>
      </Card>
    </div>
  );
};

const DailyChart = ({ summary }: { summary: AssistedRevenueSummaryDto }) => {
  const { t } = useTranslation('insights_page', { keyPrefix: 'assisted_revenue' });
  const daily = useMemo(() => groupDailyByCurrency(summary.time_series), [summary.time_series]);
  const primaryCurrency = summary.by_currency[0]?.currency;

  const chartData = daily.map((d) => ({
    day: d.day,
    value: primaryCurrency ? (d.totalsByCurrency[primaryCurrency] ?? 0) : 0,
  }));

  const axisStep = niceAxisStep(Math.max(0, ...chartData.map((d) => d.value)) / 4);
  const axisMax = axisStep * 4;
  const axisTicks = [0, axisStep, axisStep * 2, axisStep * 3, axisStep * 4];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('daily_chart_title')}</CardTitle>
        {primaryCurrency && (
          <CardDescription data-testid="daily-chart-currency">{primaryCurrency}</CardDescription>
        )}
      </CardHeader>
      <CardContent>
        <ChartContainer
          config={{
            value: { label: t('hero_label'), color: 'var(--chart-primary)' },
          }}
          className="h-60 w-full"
        >
          <AreaChart accessibilityLayer data={chartData} margin={{ left: 0, right: 12 }}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="day"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) => format(parseISO(value), 'dd MMM')}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              domain={[0, axisMax]}
              ticks={axisTicks}
              tickFormatter={(value) =>
                primaryCurrency
                  ? formatCurrencyValue(String(value), primaryCurrency)
                  : String(value)
              }
              width={56}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  indicator="dot"
                  labelFormatter={(value) => format(parseISO(value), 'dd MMM yyyy')}
                  formatter={(value) =>
                    primaryCurrency
                      ? formatCurrencyValue(String(value), primaryCurrency)
                      : String(value)
                  }
                />
              }
            />
            <Area
              dataKey="value"
              type="monotone"
              fill="var(--color-value)"
              fillOpacity={0.2}
              stroke="var(--color-value)"
              strokeWidth={2}
              dot={chartData.length <= 2}
            />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};

const RecentOrdersTable = ({ range }: { range: DateRangeState }) => {
  const { t } = useTranslation('insights_page', { keyPrefix: 'assisted_revenue' });
  const { scenario } = useParams<{ scenario: string }>();
  const { data: recent, pageIndex, setPageIndex } = useRecentAssistedOrders(range);

  if (!recent || recent.count === 0) return null;

  const totalPages = Math.ceil(recent.count / RECENT_ORDERS_PAGE_SIZE);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('recent_table_title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="recent-orders-table">
            <thead>
              <tr className="border-b text-left">
                <th className="pb-2 font-medium">{t('recent_table_header_placed_at')}</th>
                <th className="pb-2 font-medium">{t('recent_table_header_order')}</th>
                <th className="pb-2 font-medium text-right">{t('recent_table_header_amount')}</th>
                <th className="pb-2 font-medium text-right">
                  {t('recent_table_header_conversation')}
                </th>
              </tr>
            </thead>
            <tbody>
              {recent.items.map((order) => (
                <tr key={order.id} className="border-b last:border-0">
                  <td className="py-2">{format(parseISO(order.placed_at), 'dd MMM yyyy')}</td>
                  <td className="py-2">
                    {order.order_admin_url ? (
                      <a
                        href={order.order_admin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-2 hover:no-underline"
                      >
                        {order.order_number}
                      </a>
                    ) : (
                      order.order_number
                    )}
                  </td>
                  <td className="py-2 text-right">
                    {formatCurrencyValue(order.total_amount, order.currency)}
                  </td>
                  <td className="py-2 text-right">
                    {order.conversation_conv_id ? (
                      <Link
                        to={`/i/${scenario}/${IndexPath.inbox.root}/${order.conversation_conv_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-2 hover:no-underline"
                      >
                        {t('view_conversation')}
                      </Link>
                    ) : (
                      <span className="text-foreground-subtle">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center pt-4">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                    disabled={pageIndex === 0}
                  />
                </PaginationItem>
                <PaginationItem>
                  <span className="px-3 text-sm text-muted-foreground">
                    {t('pagination_page_indicator', {
                      page: pageIndex + 1,
                      total: totalPages,
                    })}
                  </span>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    onClick={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={pageIndex >= totalPages - 1}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const AssistedRevenuePage = () => {
  const { t } = useTranslation('insights_page', { keyPrefix: 'assisted_revenue' });
  const { range, handleRangeChange } = useDateRange();
  const { data: summary, isLoading } = useAssistedRevenueSummary(range);
  const { capture } = usePostHogAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasFiredViewRef = useRef(false);

  // Fire once per tab view (covers both in-app switches and email deep-link arrivals).
  useEffect(() => {
    if (hasFiredViewRef.current || isLoading || !summary) return;
    hasFiredViewRef.current = true;
    const utm = Object.fromEntries(
      DIGEST_UTM_PARAMS.map((key) => [key, searchParams.get(key)] as const),
    );
    capture(PostHogEventEnum.AssistedRevenueTabViewed, {
      has_orders: summary.total_orders > 0,
      total_orders: summary.total_orders,
      via_email: utm.utm_medium === 'email',
      ...utm,
    });
    // Consume the one-time digest UTMs so an in-app return here (Radix remounts the tab) reads them as absent.
    if (DIGEST_UTM_PARAMS.some((key) => searchParams.has(key))) {
      const next = new URLSearchParams(searchParams);
      DIGEST_UTM_PARAMS.forEach((key) => next.delete(key));
      setSearchParams(next, { replace: true });
    }
  }, [isLoading, summary, searchParams, capture, setSearchParams]);

  const hasData = summary && summary.total_orders > 0;

  return (
    <div className="max-w-7xl mx-auto py-6 space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">{t('title')}</h2>
          <p className="text-sm text-muted-foreground">{t('description')}</p>
        </div>
        <DateRangeControls range={range} onRangeChange={handleRangeChange} />
      </div>

      {!hasData ? (
        <ChartPlaceholder
          icon={ShoppingBag}
          titleTranslationKey="assisted_revenue.empty_title"
          descriptionTranslationKey="assisted_revenue.empty_description"
          spacing="large"
        />
      ) : (
        <div
          className={`space-y-6 ${isLoading ? 'animate-pulse' : ''}`}
          data-testid="assisted-revenue-content"
        >
          <HeroNumbers summary={summary} />
          <DailyChart summary={summary} />
          <RecentOrdersTable range={range} />
        </div>
      )}
    </div>
  );
};
